import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/AppDetails.css';

interface Reply {
  id: string;
  user: string;
  comment: string;
  timestamp: string;
}

interface Review {
  id: string;
  userId?: string; // ID of the user who wrote it
  user: string;
  rating: number;
  comment: string;
  likes: number;
  dislikes: number;
  voters?: { [userId: string]: 'like' | 'dislike' };
  replies: Reply[];
  avatar?: string;
}

interface AppDetailsData {
  id: string;
  name: string;
  email: string;
  category: string;
  rating: number;
  reviews: number;
  price: number;
  icon: string;
  description: string;
  detailedDescription: string;
  neighborhood?: string;
  weeklyAvailability?: Record<string, string>;
  monthlyAvailability?: Record<string, Record<string, { status: string, note: string }>>;
  screenshots: string[];
  userReviews: Review[];
  hours: string;
}

const AppDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, token } = useAuth();
  const [app, setApp] = useState<AppDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showHours, setShowHours] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = d.getDate() - day; // Start at Sunday
    return new Date(d.setDate(diff));
  });
  const [selectedDateDetails, setSelectedDateDetails] = useState<{ date: string, status: string, note: string } | null>(null);

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const fetchDetails = useCallback(() => {
    const headers: any = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    fetch(`${API_URL}/api/apps/${id}`, { headers })
      .then(res => res.json())
      .then(data => {
        if (data.error) setApp(null);
        else setApp(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching app details:', err);
        setLoading(false);
      });
  }, [id, token]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleReviewSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const reviewData = {
      user: isAnonymous ? 'Anonymous' : user?.name,
      rating: parseInt(formData.get('rating') as string),
      comment: formData.get('comment') as string,
    };

    fetch(`${API_URL}/api/apps/${id}/reviews`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify(reviewData),
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to save review');
        return data;
      })
      .then(() => {
        setReviewSubmitted(true);
        fetchDetails();
        setTimeout(() => { setShowReviewForm(false); setReviewSubmitted(false); }, 3000);
      })
      .catch(err => { console.error('Review submission error:', err); });
  };

  const handleVote = (reviewId: string, type: 'like' | 'dislike') => {
    if (!token) {
      alert('Please login to vote');
      return;
    }
    fetch(`${API_URL}/api/apps/${id}/reviews/${reviewId}/vote`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ type }),
    })
      .then(async res => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error);
        }
        fetchDetails();
      })
      .catch(err => alert(err.message));
  };

  const handleDeleteReview = (reviewId: string) => {
    if (!window.confirm('Are you sure you want to delete your review?')) return;
    
    fetch(`${API_URL}/api/apps/${id}/reviews/${reviewId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to delete review');
        }
        // If the server returns the updated sitter, use it immediately
        if (data.sitter) {
          setApp(data.sitter);
        } else {
          fetchDetails();
        }
      })
      .catch(err => {
        console.error('Delete error:', err);
        alert(`Could not delete: ${err.message}`);
      });
  };

  const handleReplySubmit = (reviewId: string) => {
    if (!token) return;
    if (!replyText.trim()) return;

    fetch(`${API_URL}/api/apps/${id}/reviews/${reviewId}/reply`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ comment: replyText }),
    })
      .then(() => {
        setReplyText('');
        setReplyingTo(null);
        fetchDetails();
      })
      .catch(err => console.error('Error replying:', err));
  };

  if (loading) return <div className="details-container"><div className="loading-state">Loading app details...</div></div>;
  if (!app) return <div className="details-container"><div className="error-state"><h2>Sitter not found</h2><Link to="/">Go back home</Link></div></div>;

  return (
    <div className="details-container">
      <Link to="/" className="back-link">← Back to Discover</Link>
      
      <section className="app-header">
        <img src={app.icon} alt={`${app.name} icon`} className="details-icon" />
        <div className="header-info">
          <h1>{app.name}</h1>
          <p className="category">{app.category}</p>
          <div className="stats">
            <div className="stat-item">
              <span className="stat-value">${app.price}</span>
              <span className="stat-label">per hour</span>
            </div>
            {app.rating > 0 ? (
              <div className="stat-item">
                <span className="stat-value">{app.rating.toFixed(1)} ★</span>
                <span className="stat-label">{app.reviews} Ratings</span>
              </div>
            ) : (
              <div className="stat-item">
                <span className="stat-value">New</span>
                <span className="stat-label">No ratings yet</span>
              </div>
            )}
          </div>
          <div className="cta-buttons">
            <Link to={`/book/${app.id}`} className="download-btn">Book Sitter</Link>
            <button className="secondary-btn" onClick={() => setShowHours(!showHours)}>
              {showHours ? 'Hide Hours' : 'View Hours'}
            </button>
          </div>
          {app.neighborhood && (
            <div style={{ marginTop: '15px', color: '#666', fontWeight: 'bold' }}>
              📍 Neighborhood: <span style={{ color: 'var(--primary-color)' }}>{app.neighborhood}</span>
            </div>
          )}
          {showHours && <div className="hours-display"><strong>Available Hours:</strong> {app.hours}</div>}
        </div>
      </section>

      <section className="availability-section" style={{ marginBottom: '40px', padding: '30px', backgroundColor: '#f8faff', borderRadius: '25px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
        <h2 style={{ marginBottom: '10px' }}>📅 Weekly Availability</h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>Check which days I am available for bookings:</p>
        
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '20px', border: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <button className="secondary-btn" onClick={() => {
              const newDate = new Date(currentWeekStart);
              newDate.setDate(newDate.getDate() - 7);
              setCurrentWeekStart(newDate);
            }}>← Previous Week</button>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>
              Week of {currentWeekStart.toLocaleDateString('default', { month: 'short', day: 'numeric' })} - {new Date(new Date(currentWeekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}
            </h3>
            <button className="secondary-btn" onClick={() => {
              const newDate = new Date(currentWeekStart);
              newDate.setDate(newDate.getDate() + 7);
              setCurrentWeekStart(newDate);
            }}>Next Week →</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px', textAlign: 'center' }}>
            {daysOfWeek.map(d => (
              <div key={d} style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'var(--primary-color)', paddingBottom: '10px' }}>{d}</div>
            ))}
            {Array.from({ length: 7 }).map((_, i) => {
              const d = new Date(currentWeekStart);
              d.setDate(d.getDate() + i);
              const dateStr = d.toISOString().split('T')[0];
              const monthKey = dateStr.substring(0, 7);
              const monthData = (app.monthlyAvailability || {})[monthKey] || {};
              const dayData = (monthData as Record<string, {status: string, note: string}>)[dateStr] || { status: 'Free', note: '' };
              const isBusy = dayData.status === 'Busy';
              const isToday = new Date().toISOString().split('T')[0] === dateStr;

              return (
                <div
                  key={dateStr}
                  onClick={() => setSelectedDateDetails({ date: dateStr, ...dayData })}
                  style={{
                    padding: '12px 5px',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    backgroundColor: isBusy ? '#ffebee' : '#e8f5e9',
                    color: isBusy ? '#c62828' : '#2e7d32',
                    border: isToday ? '3px solid var(--primary-color)' : `2px solid ${isBusy ? '#e74c3c' : '#27ae60'}`,
                    fontWeight: 'bold',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>{d.getDate()}</div>
                  <div style={{ fontSize: '0.65rem', marginTop: '4px', fontWeight: 600, opacity: 0.8 }}>
                    {isBusy ? 'BUSY' : 'FREE'}
                  </div>
                  {dayData.note && <div style={{ position: 'absolute', top: '2px', right: '2px', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isBusy ? 'white' : '#3498db' }}></div>}
                </div>
              );
            })}
          </div>

          {selectedDateDetails && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '15px', border: '1px solid #ddd', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>Details for {selectedDateDetails.date}:</strong>
                <button onClick={() => setSelectedDateDetails(null)} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontWeight: 'bold', fontSize: '1.2rem' }}>×</button>
              </div>
              <div style={{ marginTop: '5px', color: selectedDateDetails.status === 'Busy' ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>
                Status: {selectedDateDetails.status}
              </div>
              {selectedDateDetails.note ? (
                <div style={{ marginTop: '5px', color: '#333' }}>
                  <strong>Notes:</strong> {selectedDateDetails.note}
                </div>
              ) : (
                <div style={{ marginTop: '5px', color: '#999', fontStyle: 'italic' }}>No additional details provided.</div>
              )}
            </div>
          )}

          <div style={{ marginTop: '25px', display: 'flex', gap: '20px', justifyContent: 'center', fontSize: '0.9rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#e8f5e9', border: '2px solid #27ae60', borderRadius: '4px' }}></div> 
              <strong>Free</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#ffebee', border: '2px solid #e74c3c', borderRadius: '4px' }}></div> 
              <strong>Busy</strong>
            </div>
          </div>
        </div>

        <p style={{ marginTop: '20px', fontSize: '0.9rem', color: '#666', backgroundColor: '#fff3cd', padding: '12px', borderRadius: '10px', display: 'inline-block', border: '1px solid #ffeeba' }}>
          ⚠️ <strong>Note:</strong> I only babysit within the <strong>{app.neighborhood || 'my local'}</strong> neighborhood.
        </p>
      </section>

      <section className="screenshots-section">
        <div className="section-header">
          <h2>Activity Photos</h2>
        </div>
        <div className="screenshots-scroll">
          {app.screenshots?.map((src, index) => (
            <div key={index} className="device-frame">
              <div className="screen">
                <img src={src} alt={`Screenshot ${index + 1}`} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="description-section">
        <h2>Description</h2>
        <p className="detailed-desc">{app.detailedDescription}</p>
      </section>

      <section className="reviews-section">
        <div className="reviews-header">
          <h2>Ratings & Reviews</h2>
          {user ? (
            <button className="write-review-btn" onClick={() => setShowReviewForm(!showReviewForm)}>
              {showReviewForm ? 'Cancel' : 'Write a Review'}
            </button>
          ) : (
            <Link to="/login" className="write-review-btn">Login to write a review</Link>
          )}
        </div>

        {showReviewForm && (
          <div className="review-form-container">
            {reviewSubmitted ? (
              <div className="success-msg">🎉 Thank you! Your review has been submitted.</div>
            ) : (
              <form className="review-form" onSubmit={handleReviewSubmit}>
                <div className="form-group">
                  <label>Your Name</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <input name="userName" type="text" value={isAnonymous ? 'Anonymous' : user?.name} readOnly required style={{ flex: 1, backgroundColor: '#f5f5f5' }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.85rem', cursor: 'pointer', margin: 0 }}>
                      <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} />
                      Post Anonymously
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label>Rating</label>
                  <select name="rating" required>
                    <option value="5">5 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="2">2 Stars</option>
                    <option value="1">1 Star</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Comment</label>
                  <textarea name="comment" placeholder="Share your experience..." required></textarea>
                </div>
                <button type="submit" className="download-btn">Submit Review</button>
              </form>
            )}
          </div>
        )}

        <div className="reviews-grid">
          {app.userReviews?.length > 0 ? (
            app.userReviews.map((review) => {
              const userVote = user && review.voters ? review.voters[user.id] : null;
              const isAuthor = user && review.userId === user.id;

              return (
                <div key={review.id} className="review-card">
                  <div className="review-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {review.avatar && <img src={review.avatar} alt="" style={{ width: '30px', height: '30px', borderRadius: '50%' }} />}
                      <div>
                        <span className="user-name" style={{ color: review.user === 'Anonymous' ? '#999' : 'inherit', fontStyle: review.user === 'Anonymous' ? 'italic' : 'normal' }}>{review.user}</span>
                        <span className="review-rating">{'★'.repeat(review.rating)}</span>
                      </div>
                    </div>
                    {isAuthor && (
                      <button 
                        onClick={() => handleDeleteReview(review.id)}
                        style={{ background: 'none', border: 'none', color: '#ff4d4d', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  <p className="review-comment">"{review.comment}"</p>
                  
                  <div className="review-actions" style={{ display: 'flex', gap: '15px', marginTop: '10px', alignItems: 'center' }}>
                    <button 
                      onClick={() => handleVote(review.id, 'like')} 
                      style={{ 
                        background: 'none', border: 'none', 
                        cursor: 'pointer', 
                        display: 'flex', alignItems: 'center', gap: '5px',
                        color: userVote === 'like' ? 'var(--primary-color)' : '#666',
                        fontWeight: userVote === 'like' ? 'bold' : 'normal',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      👍 {review.likes || 0}
                    </button>
                    <button 
                      onClick={() => handleVote(review.id, 'dislike')} 
                      style={{ 
                        background: 'none', border: 'none', 
                        cursor: 'pointer', 
                        display: 'flex', alignItems: 'center', gap: '5px',
                        color: userVote === 'dislike' ? '#e74c3c' : '#666',
                        fontWeight: userVote === 'dislike' ? 'bold' : 'normal',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      👎 {review.dislikes || 0}
                    </button>
                    <button onClick={() => setReplyingTo(replyingTo === review.id ? null : review.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-color)', fontSize: '0.85rem' }}>
                      Reply
                    </button>
                  </div>

                  {replyingTo === review.id && (
                    <div className="reply-form" style={{ marginTop: '15px', padding: '10px', borderTop: '1px solid #eee' }}>
                      <textarea 
                        placeholder="Write a reply..." 
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', minHeight: '60px' }}
                      ></textarea>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '8px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setReplyingTo(null)} className="secondary-btn" style={{ padding: '4px 12px', fontSize: '0.8rem' }}>Cancel</button>
                        <button onClick={() => handleReplySubmit(review.id)} className="download-btn" style={{ padding: '4px 12px', fontSize: '0.8rem' }}>Post Reply</button>
                      </div>
                    </div>
                  )}

                  {review.replies?.length > 0 && (
                    <div className="replies-list" style={{ marginLeft: '20px', marginTop: '15px', borderLeft: '2px solid #f0f0f0', paddingLeft: '15px' }}>
                      {review.replies.map(reply => (
                        <div key={reply.id} className="reply-item" style={{ marginBottom: '10px' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#333' }}>{reply.user}</div>
                          <p style={{ margin: '2px 0', fontSize: '0.85rem', color: '#555' }}>{reply.comment}</p>
                          <small style={{ fontSize: '0.7rem', color: '#999' }}>{new Date(reply.timestamp).toLocaleDateString()}</small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className="no-reviews">Be the first to review {app.name}!</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default AppDetails;
